/**
 * `/join/<token>` — public team-invite landing page.
 *
 * Anyone with the URL can land here (the route is whitelisted in
 * `__root.tsx` for both anonymous and signed-in visitors). The page
 * itself decides which form to show via `JoinPage`.
 *
 * Visual chrome reuses the OnboardingShell so the join surface looks
 * the same as /sign-in (one mental model for visitors hitting Flint
 * for the first time).
 *
 * @see ADR-0011 — Team invites + RBAC scope
 * @see 08-system-decisions.md §1 — phone is the credential
 * @see 08-system-decisions.md §2 — V1 roles
 */
import { createFileRoute } from "@tanstack/react-router";
import {
  OnboardingShell,
  OnboardingHeader,
  Hero,
  HeroNote,
  BrandFor,
} from "../../../features/onboarding/components/index.ts";
import "../../../features/onboarding/components/onboarding.css";
import { JoinPage } from "../../../features/join/index.ts";

export const Route = createFileRoute("/(auth)/join/$token")({
  component: JoinRoute,
});

function JoinRoute() {
  const { token } = Route.useParams();

  return (
    <OnboardingShell
      header={<OnboardingHeader />}
      brand={<BrandFor step="signIn" />}
      footer="Phone-first authentication · team invites are good for 7 days"
    >
      <Hero
        kicker="You've been invited"
        title="Join the workspace."
        description="Confirm the phone this invite was sent to. We'll text you a one-time code on WhatsApp (with SMS fallback) and add you to the workspace as soon as it's verified."
      >
        <HeroNote>You only need a phone — no password to remember.</HeroNote>
      </Hero>
      <JoinPage token={token} />
    </OnboardingShell>
  );
}
